/**
 * TypeScript interfaces for Patient Diagnosis API
 * Matches backend DTOs exactly
 *
 * @author CareX Frontend Team
 */

// ========== Enums ==========

export enum DiagnosisStatus {
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REVIEWED = 'REVIEWED',
}

export enum RiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

// ========== Tab DTOs ==========

export interface ExamVitalsDTO {
  id?: string;
  dateOfExam?: string | null; // ISO date string
  height?: string;
  weight?: string;
  bloodPressure?: string;
  bmi?: number;
  // TB Test Details
  tbTestGivenDate?: string | null;
  tbTestReadDate?: string | null;
  tbTestType?: string;
  tbTestResult?: string; // 'Negative' | 'Positive'
  tbResultsMm?: string;
  tbActionTaken?: string;
  chestXrayResults?: string;
  tbStatus?: string;
}

export interface AmbulatoryStatusDTO {
  id?: string;
  ambulatoryStatus?: string; // 'Ambulatory' | 'Nonambulatory' | 'Bedridden'
  statusBasedOn?: string | null; // 'Physical Condition' | 'Mental Condition' | 'Both'
  bedriddenIllness?: string;
  bedriddenRecoveryFromSurgery?: string;
  bedriddenOther?: string;
  bedriddenDurationDays?: number;
  bedriddenEstimatedEndDate?: string | null;
  bedriddenPermanentExplain?: string;
}

export interface DiagnosesTreatmentsDTO {
  id?: string;
  // Primary Diagnosis
  primaryDiagnosis?: string;
  primaryCanManage?: string; // 'Yes' | 'No'
  primarySupervisionNeeded?: string;
  // Secondary Diagnosis
  secondaryDiagnosis?: string;
  secondaryCanManage?: string;
  secondarySupervisionNeeded?: string;
  // Cognitive Conditions
  mildCognitiveImpairment?: boolean;
  dementia?: boolean;
  // Contagious/Infectious Disease
  contagiousTreatment?: string;
  contagiousCanManage?: string;
  contagiousSupervisionNeeded?: string;
  // Allergies
  allergiesTreatment?: string;
  allergiesCanManage?: string;
  allergiesSupervisionNeeded?: string;
  // Other Conditions
  otherConditionsTreatment?: string;
  otherConditionsCanManage?: string;
  otherConditionsSupervisionNeeded?: string;
}

export interface PhysicalHealthAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted indicators (read-only)
  hasAuditoryImpairment?: boolean;
  hasVisualImpairment?: boolean;
  hasMotorImpairment?: boolean;
  requiresContinuousBedCare?: boolean;
}

export interface MentalConditionAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted risk indicators (read-only)
  isConfused?: boolean;
  isAggressive?: boolean;
  isWandering?: boolean;
  isSuicidal?: boolean;
  riskLevel?: RiskLevel;
}

export interface CapacityMedicationAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted indicators (read-only)
  canBatheSelf?: boolean;
  canFeedSelf?: boolean;
  canAdministerMedications?: boolean;
  requiresMedicationAssistance?: boolean;
  independenceLevel?: number; // 0-100%
}

export interface HospicePhysicianInfoDTO {
  id?: string;
  receivingHospiceCare?: string; // 'Yes' | 'No'
  hospiceTerminalIllness?: string;
  physicalHealthStatus?: string; // 'Good' | 'Fair' | 'Poor'
  comments?: string;
  physicianNameAddress?: string;
  physicianTelephone?: string;
  physicianLengthOfTimePatient?: string;
  physicianSignature?: string;
  physicianDate?: string | null;
}

// ========== Main Diagnosis DTO ==========

export interface PatientDiagnosisDTO {
  id: string;
  residentId: string;
  diagnosisDate: string;
  status: DiagnosisStatus;
  completionPercentage?: number;
  completedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  // Dynamic fields from backend
  residentName?: string;
  mrnNumber?: string;
  // Some backends return core vitals at the diagnosis root (not under examVitals).
  bp?: string;
  height?: string;
  weight?: string;
  bmi?: number | string;
  room?: string;
  age?: string;
  sex?: string;
  attendingPhysician?: string;
  // Tab data (nullable - filled independently)
  examVitals?: ExamVitalsDTO;
  ambulatoryStatus?: AmbulatoryStatusDTO;
  diagnosesTreatments?: DiagnosesTreatmentsDTO;
  physicalHealthAssessment?: PhysicalHealthAssessmentDTO;
  mentalConditionAssessment?: MentalConditionAssessmentDTO;
  capacityMedicationAssessment?: CapacityMedicationAssessmentDTO;
  hospicePhysicianInfo?: HospicePhysicianInfoDTO;
  // Multi-tenancy
  tenantId?: string;
  facilityId?: string;
  clientId?: string;
  // Audit fields
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// ========== Request DTOs ==========

export interface CreatePatientDiagnosisRequest {
  residentId: string;
  diagnosisDate: string;
  examVitals?: ExamVitalsDTO; // Optional initial data
  ambulatoryStatus?: AmbulatoryStatusDTO;
  diagnosesTreatments?: DiagnosesTreatmentsDTO;
  physicalHealthAssessment?: PhysicalHealthAssessmentDTO;
  mentalConditionAssessment?: MentalConditionAssessmentDTO;
  capacityMedicationAssessment?: CapacityMedicationAssessmentDTO;
  hospicePhysicianInfo?: HospicePhysicianInfoDTO;
}

// ========== Response Wrappers ==========

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

export interface ApiError {
  message: string;
  status: number;
  timestamp?: string;
}

// ========== Pagination ==========

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}
